// useFormDraft.ts — Tallentaa lomakkeen tilan localStorage:en jokaisella muutoksella
//
// Käyttötarkoitus: kriittiset operaattorin lomakkeet (poikkeamailmoitus, mittaus),
// joihin operaattori syöttää manuaalisesti tietoa. Jos verkko katkeaa, sessio vanhentuu
// tai sovellus suljetaan vahingossa, tieto säilyy localStorage:ssa ja palautetaan
// kun lomake avataan uudelleen.
//
// Tallennus: jokaisella state-muutoksella (debounced 500 ms).
// Palautus: kun komponentti mountataan, jos localStorage:ssa on draft.
// Tyhjennys: clearDraft()-kutsulla onnistuneen tallennuksen jälkeen.
//
// HUOM: Tallenna VAIN serialisoituvat arvot — ei File-objekteja, blobeja, refit yms.

import { useEffect, useRef, useState } from 'react';

const DRAFT_PREFIX = 'savroc_draft_';
const DRAFT_DEBOUNCE_MS = 500;

interface UseFormDraftResult<T> {
  draft: T | null;
  hasDraft: boolean;
  saveDraft: (value: T) => void;
  clearDraft: () => void;
  restoreDraft: () => T | null;
}

export function useFormDraft<T>(key: string, enabled = true): UseFormDraftResult<T> {
  const fullKey = `${DRAFT_PREFIX}${key}`;
  const [draft, setDraftState] = useState<T | null>(() => readDraft<T>(fullKey));
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function readDraft<U>(k: string): U | null {
    try {
      const raw = localStorage.getItem(k);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && '__savedAt' in parsed) {
        const ageMs = Date.now() - (parsed as any).__savedAt;
        if (ageMs > 7 * 24 * 60 * 60 * 1000) {
          localStorage.removeItem(k);
          return null;
        }
        return (parsed as any).value as U;
      }
      return parsed as U;
    } catch {
      return null;
    }
  }

  function saveDraft(value: T) {
    if (!enabled) return;
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      try {
        const payload = JSON.stringify({ value, __savedAt: Date.now() });
        localStorage.setItem(fullKey, payload);
        setDraftState(value);
      } catch {
        // localStorage täynnä tai disabled — hiljainen virhe (ei kriittinen)
      }
    }, DRAFT_DEBOUNCE_MS);
  }

  function clearDraft() {
    try {
      localStorage.removeItem(fullKey);
    } catch {}
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setDraftState(null);
  }

  function restoreDraft(): T | null {
    return readDraft<T>(fullKey);
  }

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  return {
    draft,
    hasDraft: draft !== null,
    saveDraft,
    clearDraft,
    restoreDraft,
  };
}
